import { Classes, InputGroup, TextArea } from "@blueprintjs/core";
import classnames from "classnames";
import { DatePicker } from "modules/common/components/form/DatePicker";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";
import moment from "moment";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Milestone, MILESTONE_DAY_FORMAT, MILESTONE_NAME_MAX_LENGTH, MilestoneDraft } from "../models/milestones-store";
import { MilestoneAnchor, milestoneDate } from "./MilestonesLayer";
import styles from "./MilestoneEditor.module.scss";

/** Отступ окна от точки и от краёв экрана */
const GAP = 8;

interface MilestoneEditorProps {
    /** Где открыть окно: у вехи или в месте двойного клика */
    anchor: MilestoneAnchor;
    /** Вехи ячейки (в недельном масштабе их может быть несколько) или одна новая */
    items: MilestoneDraft[];
    /** «0500 Название проекта» */
    project: string;
    /** Нет права править вехи проекта — только просмотр */
    readOnly: boolean;
    onSave: (draft: MilestoneDraft) => Promise<boolean>;
    onRemove: (milestone: Milestone) => Promise<boolean>;
    onClose: () => void;
}

interface EditState {
    name: string;
    day: Date | null;
    comment: string;
}

const toState = (draft: MilestoneDraft): EditState => ({
    name: draft.name,
    day: draft.day ? moment(draft.day, MILESTONE_DAY_FORMAT).toDate() : null,
    comment: draft.comment,
});

/**
 * Окно вехи прямо на графике — белое всплывающее, как выбор проекта в Табеле План:
 * клик по вехе сразу открывает её правку, без модального окна. Если в неделе несколько
 * вех, сверху их список — клик переключает, какую править.
 */
export const MilestoneEditor: React.FC<MilestoneEditorProps> = (props) => {
    const { anchor, items, project, readOnly, onSave, onRemove, onClose } = props;

    const [index, setIndex] = useState(0);
    const current = items[Math.min(index, items.length - 1)];
    const [state, setState] = useState<EditState>(() => toState(current));
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [position, setPosition] = useState<React.CSSProperties>({ visibility: "hidden" });

    const rootRef = useRef<HTMLDivElement>(null);
    const nameRef = useRef<HTMLInputElement>(null);

    const isNew = !current.id;

    // переключили веху в списке — в поля её значения
    useEffect(() => {
        setState(toState(current));
        setError("");
    }, [current]);

    useEffect(() => {
        !readOnly && nameRef.current && nameRef.current.focus();
    }, [readOnly, current]);

    // под точкой, а у нижнего края — над ней; по горизонтали не вылезает за экран
    useLayoutEffect(() => {
        const element = rootRef.current;

        if (!element) {
            return;
        }

        const { width, height } = element.getBoundingClientRect();
        const left = Math.max(GAP, Math.min(anchor.x - width / 2, window.innerWidth - width - GAP));
        const below = anchor.bottom + GAP;
        const top = below + height + GAP <= window.innerHeight ? below : Math.max(GAP, anchor.top - height - GAP);

        setPosition({ left, top, visibility: "visible" });
    }, [anchor, items.length]);

    // клик мимо окна закрывает его; календарь даты живёт в своём портале — его не считаем
    useEffect(() => {
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Element | null;

            if (!target || !rootRef.current || rootRef.current.contains(target)) {
                return;
            }

            if (target.closest(`.${Classes.PORTAL}`) || target.closest("[data-milestone]")) {
                return;
            }

            onClose();
        };

        document.addEventListener("pointerdown", onPointerDown, true);
        return () => document.removeEventListener("pointerdown", onPointerDown, true);
    }, [onClose]);

    const save = useCallback(async () => {
        const name = state.name.trim();

        if (!name) {
            setError("Укажите название вехи");
            return;
        }

        if (!state.day) {
            setError("Укажите дату вехи");
            return;
        }

        setBusy(true);

        const saved = await onSave({
            ...current,
            name,
            day: moment(state.day).format(MILESTONE_DAY_FORMAT),
            comment: state.comment.trim(),
        });

        setBusy(false);
        saved && onClose();
    }, [state, current, onSave, onClose]);

    const remove = useCallback(async () => {
        setBusy(true);
        const removed = await onRemove(current as Milestone);
        setBusy(false);
        removed && onClose();
    }, [current, onRemove, onClose]);

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
            }
        },
        [onClose]
    );

    return ReactDOM.createPortal(
        <div ref={rootRef} className={styles.editor} style={position} onKeyDown={onKeyDown}>
            <div className={styles.project} title={project}>
                {project}
            </div>

            {items.length > 1 && (
                <div className={styles.list}>
                    {items.map((item, position) => (
                        <div
                            key={item.id}
                            className={classnames(styles.listItem, { [styles.listItemActive]: position === index })}
                            onClick={() => setIndex(position)}
                        >
                            <span className={styles.listDate}>{milestoneDate(item)}</span>
                            <span className={styles.listName}>{item.name}</span>
                        </div>
                    ))}
                </div>
            )}

            <label className={styles.label}>Название</label>
            <InputGroup
                inputRef={nameRef}
                className="planr-default-input"
                value={state.name}
                maxLength={MILESTONE_NAME_MAX_LENGTH}
                placeholder="Например, выдача ПД заказчику"
                autoComplete="off"
                data-lpignore="true"
                disabled={readOnly}
                onChange={(event) => setState({ ...state, name: event.target.value })}
                onKeyDown={(event) => event.key === "Enter" && !readOnly && save()}
            />

            <label className={styles.label}>Дата</label>
            {/* как на остальных формах: обёртка input-group и planr-default-input */}
            <div className={Classes.INPUT_GROUP}>
                <DatePicker
                    value={state.day}
                    readOnly={readOnly}
                    clasName="planr-default-input"
                    onChange={(day) => setState({ ...state, day })}
                />
            </div>

            <label className={styles.label}>Комментарий</label>
            <div className={`${Classes.INPUT_GROUP} planr-default-input`}>
                <TextArea
                    value={state.comment}
                    autoResize={true}
                    className={Classes.FILL}
                    disabled={readOnly}
                    onChange={(event) => setState({ ...state, comment: event.target.value })}
                />
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {!readOnly && (
                <div className={styles.footer}>
                    {/* удаление — красной кнопкой с подтверждением, как на остальных формах */}
                    {!isNew && (
                        <RemoveConfirmation<boolean>
                            what={() => `веху «${current.name}»`}
                            onConfirmed={remove}
                            render={({ confirmRemoving }) => (
                                <PlanrButton
                                    type="danger"
                                    size="small"
                                    onClick={() => confirmRemoving(true)}
                                    disabled={busy}
                                >
                                    Удалить
                                </PlanrButton>
                            )}
                        />
                    )}

                    <div className={styles.spacer} />

                    <PlanrButton type="graybtn" size="small" onClick={onClose} disabled={busy}>
                        Отмена
                    </PlanrButton>

                    <PlanrButton type="greenish" size="small" onClick={save} disabled={busy}>
                        Сохранить
                    </PlanrButton>
                </div>
            )}
        </div>,
        document.body
    );
};
